import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { isEmpty } from 'lodash';
import {
  ParticipantService,
  UserDefinedFieldValuesService,
  UserDefinedFieldsService,
  UserDefinedFieldsSharedService,
  CountryService,
  ResidenceStatusService
} from '@wealthlane/api-services/wealthlane-customer-service';
import { createEntityQuery } from '../../createEntityQuery';

// ---------------------------------------------------------------------------
// Internal lookup hooks (self-contained, no handleRequest threading)
// ---------------------------------------------------------------------------

const useCountryOptions = createEntityQuery<any[], void>({
  queryKey: ['getCountries'],
  queryFn: () => CountryService.getApiCustomerApiAppCountryCountries(),
  options: { staleTime: 1000 * 60 * 30 },
});

const useResidenceOptions = createEntityQuery<any[], void>({
  queryKey: ['residenceStatusOptions'],
  queryFn: () =>
    ResidenceStatusService.getApiCustomerApiAppResidenceStatusResidenceStatusList(),
  options: { staleTime: 1000 * 60 * 30 },
});

const useUdfModules = createEntityQuery<any[], void>({
  queryKey: ['getUdfModules'],
  queryFn: () =>
    UserDefinedFieldsService.getApiCustomerApiAppUserDefinedFieldsUserDefinedModules(),
  options: { staleTime: 1000 * 60 * 30 },
});

const useUdfFieldsByModuleId = createEntityQuery<any[], number>({
  queryKey: ['getUserDefinedFieldsByModuleId'],
  queryFn: (moduleId) =>
    UserDefinedFieldsSharedService.getApiCustomerApiAppUserDefinedFieldsUserDefinedFieldsByModuleId(
      moduleId!
    ),
});

// ---------------------------------------------------------------------------
// Pure helpers (no React dependency)
// ---------------------------------------------------------------------------

function phoneFormatWithDash(value: string): string {
  const n = value?.replaceAll(/[^\d]/g, '');
  return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6, 10)}`;
}

function ssnWithDash(value: string, view: string): string {
  const n = value?.replaceAll(/[^\d]/g, '');
  return view === 'show'
    ? `${n.slice(0, 3)}-${n.slice(3, 5)}-${n.slice(5, 9)}`
    : `XXX-XXX-${n.slice(5, 9)}`;
}

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

/**
 * Fetches participant data for edit mode using the **Update** endpoint
 * and replicates the legacy data transformation: UDF flattening,
 * phone/SSN formatting, and country-flag enrichment.
 *
 * Lives in `@packages/api-client/hooks/participant/` because it is a
 * cross-app-capable API data hook grouped by business entity.
 */
const useGetEditParticipantData = (participantId: string) => {
  const { data: rawCountries } = useCountryOptions(undefined, {
    select: (data: any[]) => {
      if (data == null) return undefined;
      const sorted = [...data];
      const usIdx = sorted.findIndex((i) => i.name === 'United States Of America');
      if (usIdx > 0) sorted.unshift(sorted.splice(usIdx, 1)[0]);
      return sorted.map((item: any) => ({
        label: item.name ?? '',
        value: item.id ?? '',
        countryCodeValue: item.countryCallingCode ?? '',
        isOtherCountry: item.isOtherCountry ?? false,
        abbreviation: item?.abbreviation,
      }));
    },
  });

  const { data: residenceOptions } = useResidenceOptions(undefined, {
    select: (data: any[]) =>
      data?.map((item: any) => ({
        label: item.name ?? '',
        value: item.id ?? '',
      })),
  });

  const { data: moduleOptions } = useUdfModules(undefined, {
    select: (data: any[]) =>
      data?.map((item: any) => ({
        label: item.name ?? '',
        value: item.id ?? '',
      })),
  });

  const moduleId = useMemo(
    () => moduleOptions?.find((o: any) => o.value === 1)?.value as number | undefined,
    [moduleOptions]
  );

  const { data: udfFields } = useUdfFieldsByModuleId(moduleId!, {
    enabled: moduleId != null,
  });

  const countryOptions = rawCountries;
  const lookupsReady = !!countryOptions && !!residenceOptions && !!udfFields && moduleId != null;

  const query = useQuery(
    ['getEditParticipantData', participantId, lookupsReady],
    async () => {
      const [participant, udfValues] = await Promise.all([
        ParticipantService.getApiCustomerApiAppParticipantUpdateParticipantById(participantId),
        UserDefinedFieldValuesService.getApiCustomerApiAppUserDefinedFieldValuesUserDefinedFieldValues(
          moduleId!,
          participantId
        ),
      ]);

      if (participant == null) return null;

      const udfAddedLater = udfFields?.filter(
        (field: any) =>
          !(udfValues?.some(
            (val: any) => field.id === val.userDefinedFieldId
          ) as boolean)
      );

      const filteredData = udfValues?.map((udfField: any) => {
        const fieldName = udfField?.fieldName?.replaceAll(' ', '');
        const udfName: any =
          Boolean(fieldName) &&
          String(fieldName.charAt(0).toLowerCase()) + String(fieldName.slice(1));
        return {
          [udfName]: udfField?.value,
          [`${udfName as string}Id`]: udfField?.id,
        };
      });

      const udfAddedLaterPayload = udfAddedLater?.map((item: any) => {
        const fieldName = item?.fieldName?.replaceAll(' ', '');
        const udfName: any =
          fieldName != null && fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
        return { [udfName]: '' };
      });

      const newUdfResp =
        !isEmpty(udfAddedLaterPayload) &&
        udfAddedLaterPayload?.reduce((e: any, r: any) => ({ ...e, ...r }));

      const udfValuesResp =
        !isEmpty(filteredData) &&
        filteredData?.reduce((e: any, r: any) => ({ ...e, ...r }));

      const p = participant as any;

      const countryCodeValue = countryOptions?.find(
        (obj: any) => obj?.value === p?.workNumberCountryCodeId
      )?.isOtherCountry;

      const enriched = {
        ...p,
        participantOtherCountry: countryOptions?.find(
          (obj: any) => obj?.value === p?.countryId
        )?.isOtherCountry,
        isOtherCountry: countryOptions?.find(
          (obj: any) => obj?.value === p?.employmentCountryId
        )?.isOtherCountry,
        isOtherCountryPhone: countryCodeValue,
        workNumber:
          p?.workNumber?.includes('x') === true
            ? p?.workNumber
            : !isEmpty(p?.workNumber)
              ? countryCodeValue ?? false
                ? p?.workNumber
                : phoneFormatWithDash(p?.workNumber)
              : '',
        ssn:
          p?.ssn === 'xxx-xx-xxxx'
            ? p?.ssn
            : !isEmpty(p?.ssn)
              ? countryOptions?.find((obj: any) => obj?.value === p?.countryId)?.isOtherCountry
                ? p?.ssn
                : ssnWithDash(p?.ssn, 'show')
              : '',
        id: p?.participantId,
        residentStatusName: residenceOptions?.find(
          (obj: any) => obj?.value === p?.residentStatusId
        )?.label,
      };

      return {
        ...enriched,
        ...(udfValuesResp || {}),
        ...(newUdfResp || {}),
      };
    },
    {
      enabled: !!participantId && lookupsReady,
    }
  );

  return query;
};

export default useGetEditParticipantData;
